import React, { Suspense } from 'react';
import { serializeAttributes, toPascalCase } from './utils.js';
import { BlockContext, BlockRender } from './BlockViewer.js';
import { createContext } from 'react';
export const componentStorageReducer = (storage = [], action) => {
    if (!action.component)
        return storage;
    switch (action.type) {
        case 'REGISTER':
            return [...storage, action.component];
        case 'UNREGISTER':
            const index = storage.indexOf(action.component);
            if (index > -1) {
                storage.splice(index, 1);
            }
            return [...storage];
    }
    return storage;
};
export const ToolbarContext = createContext(undefined);
export const InspectorContext = createContext(undefined);
async function BlockEditor({ block: Block }) {
    let [editorApi, componentsApi] = await Promise.all([await import('./wordpress/editorApi.js'), await import('./wordpress/components.js')]);
    const BlockControls = editorApi.BlockControls;
    const InspectorControls = editorApi.InspectorControls;
    let editor = ({ attributes, ...props }) => {
        const gutenbergBlockProps = editorApi.useBlockProps();
        const [inspectors, inspectorDispatch] = React.useReducer(componentStorageReducer, []);
        const [toolbars, toolbarsDispatch] = React.useReducer(componentStorageReducer, []);
        const blockProps = { __gutenbergEditor: { attributes, ...props }, ...attributes };
        return (React.createElement(BlockContext.Provider, { value: {
                id: props.clientId,
                Block,
                attributes: blockProps,
                gutenbergBlockProps
            } },
            React.createElement(BlockControls, null, toolbars.map((v, i) => (React.createElement(Suspense, { fallback: React.createElement(componentsApi.Spinner, null), key: i }, React.createElement(v))))),
            React.createElement(InspectorControls, null, inspectors.map((v, i) => (React.createElement(Suspense, { fallback: React.createElement(componentsApi.Spinner, null), key: i }, React.createElement(v))))),
            React.createElement(ToolbarContext.Provider, { value: { state: toolbars, dispatch: toolbarsDispatch } },
                React.createElement(InspectorContext.Provider, { value: { state: inspectors, dispatch: inspectorDispatch } },
                    React.createElement(BlockRender, null)))));
    };
    Object.defineProperty(editor, 'name', { value: 'BlockEditor-' + Block.name });
    return editor;
}
export const outOfScope = (data) => {
    console.error('[Smartfire Engine] You tried to set data inside a non editor environment', data);
};
export const edit = async function (block) {
    return await BlockEditor({ block });
};
// If you change this function, please correct the depreciation element below.
export const save = async function (_block, _name) {
    const InnerBlocks = (await import('./wordpress/editorApi.js')).InnerBlocks;
    return function () {
        return (React.createElement(InnerBlocks.Content, null));
    };
};
/**
 * Backward compatibility is to ensure a smooth experience when we upgrade the save function.
 * If you do so, please upgrade that function array accordingly.
 * <ul>
 *     <li>versions is the range of valid versions for this save method
 *     <li>save is the applied save method on this versions
 * </ul>
 * @type {{versions: string, save: function}[]}
 */
export const backward_compatibility = async () => {
    const InnerBlocks = (await import('./wordpress/editorApi.js')).InnerBlocks;
    return [
        {
            versions: '3.0.0 - 5.0.0',
            save: function (_block, name) {
                return function ({ attributes }) {
                    const serializedName = toPascalCase(name);
                    return (React.createElement("div", { "data-smartfire-type": serializedName },
                        React.createElement("div", { dangerouslySetInnerHTML: {
                                __html: `<!-- This is a smartfire component rendered on runtime (see https://smartfire.pro). -->`,
                            } }),
                        React.createElement("div", { className: "smartfireBlockChildren", style: { display: 'none' } },
                            React.createElement(InnerBlocks.Content, null)),
                        React.createElement("script", { dangerouslySetInnerHTML: {
                                __html: '(function(){smartfire.render("' +
                                    serializedName +
                                    '", ' +
                                    serializeAttributes({ attributes }) +
                                    ')})()',
                            } })));
                };
            },
        },
        {
            versions: '2.2.0 - 3.0.0',
            save: function (_block, name) {
                return function ({ attributes }) {
                    const serializedName = toPascalCase(name);
                    return (React.createElement("div", { "data-smartfire-type": serializedName },
                        React.createElement("div", { dangerouslySetInnerHTML: {
                                __html: `<!-- This is a smartfire component rendered on runtime (see https://smartfire.pro). -->`,
                            } }),
                        React.createElement("div", { className: "smartfireBlockChildren", style: { display: 'none' } },
                            React.createElement(InnerBlocks.Content, null)),
                        React.createElement("script", { dangerouslySetInnerHTML: {
                                __html: '(function(){smartfire.render("' +
                                    serializedName +
                                    '", ' +
                                    serializeAttributes({ attributes }) +
                                    ')})()',
                            } })));
                };
            },
        },
        {
            versions: '2.0.0 - 2.1.2',
            save: function (_block, name) {
                return function ({ attributes }) {
                    const serializedName = toPascalCase(name);
                    return (React.createElement("div", { "data-smartfire-type": serializedName },
                        React.createElement("div", { dangerouslySetInnerHTML: {
                                __html: `<!-- Hello editor,
    this code is designed to directly call Smartfire library, we advise you to not try
    to edit it. It will be recovered from the initial state.-->`,
                            } }),
                        React.createElement("div", { className: "smartfireBlockChildren", style: { display: 'none' } },
                            React.createElement(InnerBlocks.Content, null)),
                        React.createElement("script", { dangerouslySetInnerHTML: {
                                __html: '(function(){smartfire.render("' +
                                    serializedName +
                                    '", ' +
                                    JSON.stringify({ attributes }) +
                                    ')})()',
                            } })));
                };
            },
        },
    ];
};

//# sourceMappingURL=BlockEditor.js.map
